import React, { useState, useEffect, useCallback } from "react";
import { Menu, X, ShoppingBag, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Link, useLocation } from "react-router-dom";

const CATEGORY_LINKS = [
  { to: "/peptides-for-med-spas", label: "All Med Spa Peptides", testid: "nav-cat-hub" },
  { to: "/anti-aging-peptides", label: "Anti-Aging & Skin", testid: "nav-cat-anti-aging" },
  { to: "/weight-loss-peptides", label: "Weight Loss & Metabolism", testid: "nav-cat-weight-loss" },
  { to: "/recovery-peptides", label: "Recovery & Repair", testid: "nav-cat-recovery" },
  { to: "/hormone-peptides", label: "Hormone & Longevity", testid: "nav-cat-hormone" },
  { to: "/sexual-wellness-peptides", label: "Sexual Wellness", testid: "nav-cat-sexual-wellness" },
  { to: "/topical-peptides", label: "Topical Peptides", testid: "nav-cat-topicals" },
  { to: "/peptide-glossary", label: "Peptide Glossary", testid: "nav-cat-glossary" },
  { to: "/locations", label: "By State (50 + DC)", testid: "nav-cat-locations" },
  { to: "/for-med-spas", label: "For Med Spa Owners", testid: "nav-cat-for-med-spas" },
  { to: "/for-providers", label: "For Licensed Providers", testid: "nav-cat-for-providers" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCatMenuOpen, setIsCatMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-[#fffaf5]/95 backdrop-blur-md border-b border-[#f0d8d4]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-2" data-testid="header-logo">
            <div className="text-2xl font-bold bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent">
              Peptides For Med Spas
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200" data-testid="nav-home">
              Home
            </Link>
            <Link to="/shop" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200" data-testid="nav-shop">
              Shop
            </Link>

            {/* Categories dropdown — SEO pillar pages */}
            <div
              className="relative"
              onMouseEnter={() => setIsCatMenuOpen(true)}
              onMouseLeave={() => setIsCatMenuOpen(false)}
            >
              <button
                className="flex items-center text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200"
                data-testid="nav-categories-toggle"
                onClick={() => setIsCatMenuOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={isCatMenuOpen}
              >
                Categories
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isCatMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {isCatMenuOpen && (
                <div
                  className="absolute top-full left-0 pt-2 w-72 z-50"
                  data-testid="nav-categories-menu"
                >
                  <div className="bg-white border border-[#f0d8d4] rounded-xl shadow-xl py-2 overflow-hidden">
                    {CATEGORY_LINKS.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={c.testid}
                        className="block px-4 py-2.5 text-sm text-[#5a4a40] hover:bg-[#fdf2f4] hover:text-[#b89a6d] transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200" data-testid="nav-blog">
              Blog
            </Link>
            <Link to="/recovery-guide" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200" data-testid="nav-recovery-guide">
              Guide
            </Link>
            <Link to="/about" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200" data-testid="nav-about">
              About
            </Link>
            <Link to="/shop" data-testid="nav-shop-cta">
              <Button className="bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Shop Now
              </Button>
            </Link>
          </nav>

          <button
            className="md:hidden text-[#5a4a40] hover:text-[#b89a6d]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#f0d8d4] max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
            <nav className="flex flex-col space-y-3">
              <Link to="/" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left" onClick={() => setIsMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/shop" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left" onClick={() => setIsMobileMenuOpen(false)}>
                Shop
              </Link>
              <div className="pt-2 pb-1 text-xs uppercase tracking-wider text-[#a09186] font-semibold">Categories</div>
              {CATEGORY_LINKS.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`mobile-${c.testid}`}
                  className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left pl-3"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {c.label}
                </Link>
              ))}
              <div className="pt-2"></div>
              <Link to="/blog" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left" onClick={() => setIsMobileMenuOpen(false)}>
                Blog
              </Link>
              <Link to="/recovery-guide" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left" onClick={() => setIsMobileMenuOpen(false)}>
                Recovery Guide
              </Link>
              <button onClick={() => scrollToSection("faq")} className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left">
                FAQ
              </button>
              <button onClick={() => scrollToSection("contact")} className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left">
                Contact
              </button>
              <Link to="/about" className="text-[#5a4a40] hover:text-[#b89a6d] transition-colors duration-200 text-left" onClick={() => setIsMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/shop" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold w-full">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Shop Now
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
